import { CircularProgress, LinearProgress, Tooltip } from "@mui/material";
import DetailsPanel from "components/Elements/DetailsPanel";
import { Link } from "react-router-dom";
import AutoCompleteBox from "components/Elements/EMIChip/AutoCompleteBox";
import {
	useInfiniteQuery,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";

import {
	isoDateWithoutTimeZone,
	debounce,
	roundedToFixed,
	defaultPageSize,
	createTableSortHandler,
} from "helpers/utils";
import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import { showError } from "../../../redux/common/actions";
import {
	getCountOfServiceList,
	postNewService,
	getServiceAutocompleteSearch,
	getServicesSearch,
} from "services/services/serviceLists/index";
import AddNewServiceDetail from "./AddService";
import ImportCSVFile from "./CSVImport";
import Header from "./Header";
import { makeStyles } from "tss-react/mui";
import ServiceListTable from "./ServiceListTable";
import ServicesView from "components/Modules/Services/ServicesView";
import {
	serviceTableColumns,
	serviceTableHeader,
	statusOfServices,
} from "constants/serviceDetails";
import Icon from "components/Elements/Icon";
import { ReactComponent as DefectIcon } from "assets/icons/defects.svg";
import ProgressBar from "components/Elements/ProgressBar";
import ColourConstants from "helpers/colourConstants";
import DeleteDialog from "components/Elements/DeleteDialog";
import StatusChangePopup from "./StatusChangePopup";
import MultipleChangeStatusPopup from "./MultipleChangeStatusPopup";
import TabTitle from "components/Elements/TabTitle";
import mainAccess from "helpers/access";
import { getSiteApplicationDetail } from "services/clients/sites/siteApplications/siteApplicationDetails";
import { FilterProvider, FilterContainer, useFilters } from "components/Elements/Filters";
import { useFilterInitialization } from "components/Elements/Filters/Dropdown/hooks/useFilterInitialization";
import { ChipTypeEnums } from "constants/enums";
import {
	makeGetNextPageParam,
	removeRowFromInfiniteCache,
	flattenPages,
} from "helpers/infiniteQuery";

const useStyles = makeStyles()((theme) => ({
	loading: {
		position: "absolute",
		width: "100%",
		left: 0,
		top: 0,
		right: 0,
	},
}));

// defects.svg is fill="#fff" for use on the sidebar's red circular backdrop -
// override to a visible colour for the plain table cell here.
const useDefectIconStyles = makeStyles()(() => ({
	icon: {
		display: "inline-flex",
		"& path": {
			fill: ColourConstants.red,
		},
	},
}));

const DefectIndicator = () => {
	const { classes } = useDefectIconStyles();
	return (
		<Tooltip title="This service has a defect">
			<span className={classes.icon}>
				<DefectIcon width={20} height={20} aria-label="Has defect" />
			</span>
		</Tooltip>
	);
};

const SERVICES_LIST_KEY = ["services", "list"];
const SERVICES_COUNT_KEY = ["services", "count"];

const formattedData = (data, history) => {
	if (!data || !Array.isArray(data)) {
		return [];
	}

	return data.map((x) => ({
		...x,
		workOrder: (
			<Link
				to={`${x.id}`}
				style={{
					color: ColourConstants.activeLink,
					cursor: "pointer",
					textDecoration: "none",
				}}
			>
				{x.workOrder}
			</Link>
		),

		status: (
			<span
				style={{
					display: "inline-flex",
					gap: 5,
				}}
			>
				<Icon name={statusOfServices(x.status, x.tasksSkipped)} />
				{statusOfServices(x.status, x.tasksSkipped)}
			</span>
		),
		hasDefects: x.hasDefects ? <DefectIndicator /> : null,
		percentageComplete: (
			<span style={{ width: "100%" }}>
				<ProgressBar
					value={roundedToFixed(x?.percentageComplete, 0)}
					customLabel={x?.percentageComplete === 100 ? "Complete" : undefined}
					height="30px"
					width={
						x.percentageComplete === 0 || x.percentageComplete > 20
							? "125px"
							: "50px"
					}
					bgColour={
						x?.percentageOverTime <= 5
							? "#23BB79"
							: x?.percentageOverTime > 5 && x?.percentageOverTime <= 10
							? "#ED8738"
							: "#E31212"
					}
					baseBgColor="#fafafa"
					labelAlignment={
						x.percentageComplete !== 0 && x.percentageComplete < 20
							? "outside"
							: "left"
					}
					labelColor={
						x.percentageComplete === 0
							? "#000000"
							: x.percentageComplete > 20
							? "#fff"
							: x?.percentageOverTime <= 5
							? "#23BB79"
							: x?.percentageOverTime > 5 && x?.percentageOverTime <= 10
							? "#ED8738"
							: "#E31212"
					}
					borderRadius="20px"
				/>
			</span>
		),
		percentageOverTime: (
			<span
				style={{
					color:
						x?.percentageOverTime <= 5
							? "#23BB79"
							: x?.percentageOverTime > 5 && x?.percentageOverTime <= 10
							? "#ED8738"
							: "#E31212",
				}}
			>
				{x?.percentageOverTime !== null ? `${x?.percentageOverTime}%` : ""}
			</span>
		),
		minutesOverTime: (
			<span
				style={{
					color:
						x?.percentageOverTime <= 5
							? "#23BB79"
							: x?.percentageOverTime > 5 && x?.percentageOverTime <= 10
							? "#ED8738"
							: "#E31212",
				}}
			>
				{x?.minutesOverTime !== null ? `${x?.minutesOverTime}` : ""}
			</span>
		),

		scheduledDate: isoDateWithoutTimeZone(x.scheduledDate + "Z"),
		estimatedMinutes: roundedToFixed(x.estimatedMinutes, 1),
		checkoutDate: x.checkoutDate
			? isoDateWithoutTimeZone(x.checkoutDate + "Z")
			: "",
	}));
};


// Filter configuration for the new Filter framework
const filterConfig = {
	filters: {
		serviceStatus: true,
		timeframe: {
			allowCustomRange: true,
			allowedRanges: ['today', 'yesterday', 'tomorrow', 'this-week', 'last-week', 'next-7-days', 'next-14-days', 'last-month', 'last-year', 'last-7-days', 'last-14-days', 'last-30-days', 'last-90-days', 'custom']
		},
		department: { multiSelect: true },
		quickFilter: {
			multiSelect: true,
			persistAll: true,
			showHasDefectsOnly: true,
		},
		tableSort: true,
		viewToggle: true,
		chips: {
			enabled: true,
			hiddenTypes: [ChipTypeEnums.SiteDepartmentID, ChipTypeEnums.DefectStatusID]
		}
	}
};

function ServiceListsContent() {
	const { filterValues, updateFilter, allFiltersInitialized, buildUrlParams } = useFilters();

	useFilterInitialization('chips', [], {
		getDefaultValue: () => [],
		getParams: (chips) => ({ chips }),
		waitForData: false,
	});

	useFilterInitialization('tableSort', [], {
		getDefaultValue: () => ({ id: 'scheduledDate_asc', name: 'scheduledDate (Ascending)' }),
		getParams: (sortOption) => {
			if (!sortOption.id) {
				return { sortField: 'scheduledDate', sortOrder: 'asc' };
			}
			const [sortField, sortOrder] = sortOption.id.split('_');
			return { sortField, sortOrder };
		},
		waitForData: false,
	});

	const chips = filterValues.chips?.value || [];

	const dispatch = useDispatch();
	const { classes } = useStyles();
	const queryClient = useQueryClient();

	const {
		position,
		customCaptions,
		siteAppID,
		siteID,
		application: {
			allowIndividualAssetModels,
			name,
			showArrangements,
			allowRegisterAssetsForServices,
			showAssetReferences,
		},
		site: { siteDepartmentID, siteDepartmentName, showServiceClientName },
		weekStartDay,
	} = JSON.parse(sessionStorage.getItem("me")) ||
	JSON.parse(localStorage.getItem("me"));

	let analyticsData = JSON.parse(localStorage.getItem("analyticaData"));
	const showData = analyticsData?.data;
	const [autocompleteData, setAutocompleteData] = useState([]);

	const tableSortParams = filterValues.tableSort?.params || { sortField: "scheduledDate", sortOrder: "asc" };
	const currentTableSort = [tableSortParams.sortField, tableSortParams.sortOrder];

	const handleTableSort = useCallback((sortField, sortOrder) => {
		createTableSortHandler(tableSortParams, updateFilter)(sortField, sortOrder);
	}, [tableSortParams, updateFilter]);

	const [openAddService, setOpenAddService] = useState(false);
	const [openImportCSV, setImportCSV] = useState(false);
	const [loading, setLoading] = useState(true);
	const [isSearching, setSearching] = useState(false);
	const [deleteID, setDeleteID] = useState(null);
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [selectedServices, setSelectedServices] = useState([]);
	const [openChnageStatusPopup, setOpenChnageStatusPopup] = useState(false);
	const [openMultipleChnageStatusPopup, setOpenMultipleChnageStatusPopup] =
		useState(false);

	const [dataForFetchingService, setDataForFetchingService] = useState({
		pageNumber: 1,
		pageSize: defaultPageSize(),
		search: "",
		sortField: "",
		sort: "",
	});

	const tableRef = useRef();
	const searchRef = useRef("");

	const [pageSize] = useState(() => defaultPageSize());

	const urlParams = buildUrlParams();
	// urlParams is a fresh object every render, so its stringified form is used
	// as the change signal. TanStack still does structural comparison on the
	// resolved queryKey, so duplicate fetches are deduped anyway.
	const urlParamsKey = JSON.stringify(urlParams);
	const queryInputs = useMemo(
		() => ({
			siteAppId: siteAppID,
			chips,
			...urlParams,
			sortField: tableSortParams.sortField,
			sort: tableSortParams.sortOrder,
		}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[siteAppID, chips, urlParamsKey, tableSortParams.sortField, tableSortParams.sortOrder]
	);

	const servicesQueryKey = useMemo(
		() => [...SERVICES_LIST_KEY, { ...queryInputs, pageSize }],
		[queryInputs, pageSize]
	);

	const servicesQuery = useInfiniteQuery({
		queryKey: servicesQueryKey,
		enabled: allFiltersInitialized && Boolean(siteAppID),
		initialPageParam: 1,
		queryFn: async ({ pageParam }) => {
			const response = await getServicesSearch({
				...queryInputs,
				search: searchRef.current,
				pageNumber: pageParam,
				pageSize,
			});
			if (!response?.status) {
				throw new Error(response?.data?.detail || "Failed to load services");
			}
			return { rows: Array.isArray(response.data) ? response.data : [], pageParam };
		},
		getNextPageParam: makeGetNextPageParam(pageSize),
	});

	useEffect(() => {
		if (servicesQuery.isError) {
			dispatch(showError(servicesQuery.error?.message || "Failed to load services"));
		}
	}, [servicesQuery.isError, servicesQuery.error, dispatch]);

	const countQueryKey = useMemo(
		() => [...SERVICES_COUNT_KEY, queryInputs],
		[queryInputs]
	);

	const countQuery = useQuery({
		queryKey: countQueryKey,
		enabled: allFiltersInitialized && Boolean(siteAppID),
		queryFn: async () => {
			const response = await getCountOfServiceList({
				...queryInputs,
				search: searchRef.current,
			});
			return response?.status ? response.data : 0;
		},
	});

	const allData = useMemo(() => flattenPages(servicesQuery.data), [servicesQuery.data]);
	const countOFService = countQuery.data ?? 0;

	const refreshServices = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: SERVICES_LIST_KEY });
		return queryClient.invalidateQueries({ queryKey: SERVICES_COUNT_KEY });
	}, [queryClient]);

	const handleSearch = useCallback(
		debounce(
			async (value) => {
				setSearching(true);
				searchRef.current = value;

				if (value.length >= 3) {
					const autoCompleteRes = await getServiceAutocompleteSearch({ search: value });
					setAutocompleteData(autoCompleteRes?.data || []);
				} else {
					setAutocompleteData([]);
				}

				setSearching(false);
			},
			300
		),
		[]
	);

	const handleAutoCompleteContent = async (item) => {
		const mappedChip = {
			chipType: item.chipType || (
				item.type === "modelId" ? ChipTypeEnums.ModelID :
				item.type === "siteAssetId" ? ChipTypeEnums.SiteAssetID :
				item.type === "serviceWorkOrder" ? ChipTypeEnums.ServiceWorkOrder :
				ChipTypeEnums.Undefined
			),
			id: item.id || null,
			value: item.value || ""
		};

		const currentChips = filterValues.chips?.value || [];
		const isDuplicate = currentChips.some(
			(existingItem) =>
				existingItem.id === mappedChip.id &&
				existingItem.chipType === mappedChip.chipType &&
				existingItem.value === mappedChip.value
		);

		if (!isDuplicate) {
			const updatedChips = [...currentChips, mappedChip];
			updateFilter('chips', updatedChips, { chips: updatedChips });
		}

		setAutocompleteData([]);
		setDataForFetchingService({
			pageNumber: 1,
			pageSize: defaultPageSize(),
			search: "",
			sortField: "",
			sort: "",
		});

		searchRef.current = "";
	};

	// Analytics-redirect chip auto-push (unchanged in spirit).
	useEffect(() => {
		const fetchData = async () => {
			if (analyticsData?.isRedirected) {
				const chipsToUpdate = [];

				if (showData["model"]?.siteDepartmentID) {
					let modelData = showData["model"];

					if (modelData?.id) {
						const newChipContent = {
							chipType: ChipTypeEnums.ModelID,
							id: modelData.id,
							value: modelData.name,
						};

						chipsToUpdate.push(newChipContent);
						searchRef.current = modelData.modelName;
					}
				}

				if (chipsToUpdate.length > 0) {
					const currentChips = filterValues.chips?.value || [];
					const updatedChips = [...currentChips, ...chipsToUpdate];
					updateFilter('chips', updatedChips, { chips: updatedChips });
				}
			}
		};
		fetchData();
		return () => {
			localStorage.setItem(
				"analyticaData",
				JSON.stringify({ data: showData, isRedirected: false })
			);
		};
	}, [analyticsData?.data, filterValues.chips, updateFilter]);

	useEffect(() => {
		setLoading(false);
		document.body.style.overflowX = "hidden";
		document.body.style.maxWidth = "100%";
	}, [siteID]);

	const fetchSiteApplicationDetails = async () => {
		try {
			const result = await getSiteApplicationDetail(siteAppID);
			const localItems =
				JSON.parse(sessionStorage.getItem("me")) ||
				JSON.parse(localStorage.getItem("me"));

			const updatedStorage = {
				...localItems,
				site: {
					...localItems.site,
					showServiceClientName: result?.data?.showServiceClientName,
				},
				application: {
					...localItems.application,
					allowFacilityBasedModels:
						result?.data?.application.showServiceClientName,
					allowIndividualAssetModels:
						result?.data?.application.allowIndividualAssetModels,
					allowRegisterAssetsForServices:
						result?.data?.application.allowRegisterAssetsForServices,
				},
				weekStartDay: result?.data?.weekStartDay ?? 0,
			};
			localStorage.setItem("me", JSON.stringify(updatedStorage));
			sessionStorage.setItem("me", JSON.stringify(updatedStorage));
		} catch (err) {
			dispatch(showError(err?.response?.detail || "Failed to fetch data"));
		}
	};

	useEffect(() => {
		fetchSiteApplicationDetails();
	}, []);

	const addNewService = async (payload) => {
		return await postNewService(payload);
	};

	const handleDeleteDialogOpen = (id) => {
		setDeleteID(id);
		setOpenDeleteDialog(true);
	};

	const handleChangeStatus = (id, name, changeTostatus) => {
		setDeleteID({ id, name, changeTostatus });
		setOpenChnageStatusPopup(true);
	};

	const handleRemoveData = (id) => {
		removeRowFromInfiniteCache(queryClient, servicesQueryKey, (row) => row.id === id);
		queryClient.invalidateQueries({ queryKey: SERVICES_COUNT_KEY });
	};

	const handleSelectService = (service) => {
		if (selectedServices.findIndex((x) => x.id === service.id) === -1) {
			setSelectedServices((prev) => [...prev, service]);
		} else {
			setSelectedServices((prev) => prev.filter((x) => x.id !== service.id));
		}
	};

	const isServicesStopped = () => {
		const isAllStopped = selectedServices[0]?.status === "T";
		if (isAllStopped) {
			return selectedServices.every(
				(x) => x.tasksSkipped === selectedServices[0].tasksSkipped
			);
		}
		return true;
	};

	const isMultipleServiceDisable = () => {
		if (selectedServices.length === 0) return true;
		return !(
			selectedServices.every(
				(x) => x?.status === selectedServices?.[0]?.status
			) && isServicesStopped()
		);
	};

	if (loading) return <CircularProgress />;

	const tableIsRefreshing =
		(servicesQuery.isFetching && !servicesQuery.isFetchingNextPage) ||
		countQuery.isFetching;

	return (
		<>
			<TabTitle title={`${customCaptions.servicePlural} | ${name}`} />
			{(isSearching || tableIsRefreshing) && <LinearProgress className={classes.loading} />}
			<StatusChangePopup
				open={openChnageStatusPopup}
				title={`Change Status to ${deleteID?.name}`}
				onClose={() => setOpenChnageStatusPopup(false)}
				siteAppID={siteAppID}
				fetchData={refreshServices}
				setDataForFetchingService={setDataForFetchingService}
				serviceId={deleteID}
			/>
			<MultipleChangeStatusPopup
				customCaptions={customCaptions}
				onClose={() => setOpenMultipleChnageStatusPopup(false)}
				open={openMultipleChnageStatusPopup}
				status={selectedServices[0]?.status}
				siteAppID={siteAppID}
				services={selectedServices}
				fetchData={() => {
					setSelectedServices([]);
					return refreshServices();
				}}
				setSelectedServices={setSelectedServices}
			/>

			<DeleteDialog
				entityName="Service"
				open={openDeleteDialog}
				closeHandler={() => setOpenDeleteDialog(false)}
				deleteID={deleteID}
				deleteEndpoint={"/api/services"}
				handleRemoveData={handleRemoveData}
			/>
			<ImportCSVFile
				open={openImportCSV}
				handleClose={() => {
					setImportCSV(false);
				}}
				importSuccess={async () => {
					await refreshServices();
					setDataForFetchingService({
						pageNumber: 1,
						pageSize: defaultPageSize(),
						search: "",
						sortField: "",
						sort: "",
					});
				}}
				siteAppID={siteAppID}
			/>
			<AddNewServiceDetail
				open={openAddService}
				closeHandler={() => setOpenAddService(false)}
				siteAppId={siteAppID}
				title={"Add " + customCaptions?.service}
				createProcessHandler={addNewService}
				showArrangements={showArrangements}
				showServiceClientName={showServiceClientName}
				allowRegisterAssetsForServices={allowRegisterAssetsForServices}
				customCaptions={customCaptions}
				position={position}
				setSearchQuery={() => {}}
				showAssetReferences={showAssetReferences}
				fetchData={refreshServices}
				setDataForFetchingService={setDataForFetchingService}
			/>
			<div className="container">
				<Header
					setOpenMultipleChnageStatusPopup={setOpenMultipleChnageStatusPopup}
					MultipleChangeStatusDisabled={isMultipleServiceDisable()}
					customCaptions={customCaptions}
					setOpenAddService={setOpenAddService}
					setImportCSV={setImportCSV}
					dataLength={countOFService}
					selectedServices={selectedServices}
					searchFilter={searchRef.current}
					siteAppID={siteAppID}
				/>
				<div
					className="detailsContainer"
					style={{ alignItems: "center", marginTop: "-15px" }}
				>
					<DetailsPanel
						showHeader={false}
						description={`View all ${customCaptions?.servicePlural} across your operations`}
					/>
					<AutoCompleteBox
						searchQuery={dataForFetchingService?.search}
						setSearchQuery={(e) => {
							e.persist();
							const searchValue = e.target.value;
							setDataForFetchingService((prev) => ({
								...prev,
								search: searchValue,
							}));

							if (searchValue.length >= 3 || searchValue === '') {
								handleSearch(searchValue);
							}
						}}
						width="100%"
						required
						label="Services Access"
						setSelectedValue={handleAutoCompleteContent}
						selectdValueToshow="name"
						dataSource={autocompleteData}
						isSearching={isSearching}
						chips={chips}
					/>
				</div>

				{/* Filter Framework with ChipsFilter */}
				<div style={{ marginTop: 20 }}>
					<FilterContainer />
				</div>
				<ServicesView>
					<ServiceListTable
						ref={tableRef}
						data={allData}
						headers={serviceTableHeader(
							allowIndividualAssetModels,
							customCaptions,
							showServiceClientName
						)}
						columns={serviceTableColumns(
							allowIndividualAssetModels,
							showServiceClientName
						)}
						formattedData={formattedData}
						handleDeleteDialogOpen={handleDeleteDialogOpen}
						handleChnageStatus={handleChangeStatus}
						handleSort={handleTableSort}
						selectedServices={selectedServices}
						currentTableSort={currentTableSort}
						handleSelectService={handleSelectService}
						isReadOnly={position?.[mainAccess.serviceAccess] === "R"}
						hasNextPage={servicesQuery.hasNextPage}
						isFetchingNextPage={servicesQuery.isFetchingNextPage}
						fetchNextPage={servicesQuery.fetchNextPage}
						isFetchNextPageError={servicesQuery.isFetchNextPageError}
					/>
				</ServicesView>
			</div>
		</>
	);
}

// Wrapper component that provides FilterContext
function ServiceLists() {
	return (
		<FilterProvider
			config={filterConfig}
			storageKey="services"
		>
			<ServiceListsContent />
		</FilterProvider>
	);
}

export default ServiceLists;
